import React, { useEffect, useMemo, useState } from 'react';
import './SettingsPage.css';

type DigestFrequency = 'off' | 'daily' | 'weekly';

interface CommunitySettingsState {
  notifications: {
    mentions: boolean;
    likes: boolean;
    follows: boolean;
    system: boolean;
    digest: DigestFrequency;
  };
  content: {
    safeMode: boolean;
    showFollowedOnly: boolean;
    muteCommercial: boolean;
  };
  privacy: {
    profileVisibility: 'public' | 'followers' | 'private';
    allowMessages: boolean;
  };
}

const STORAGE_KEY = 'community.settings.v1';

const DEFAULT_SETTINGS: CommunitySettingsState = {
  notifications: {
    mentions: true,
    likes: true,
    follows: true,
    system: true,
    digest: 'daily',
  },
  content: {
    safeMode: true,
    showFollowedOnly: false,
    muteCommercial: false,
  },
  privacy: {
    profileVisibility: 'public',
    allowMessages: true,
  },
};

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<CommunitySettingsState>(DEFAULT_SETTINGS);
  const [dirty, setDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as CommunitySettingsState;
        setSettings({ ...DEFAULT_SETTINGS, ...parsed });
      }
    } catch (error) {
      console.warn('Failed to read community settings', error);
    }
  }, []);

  const handleNotificationToggle = (key: 'mentions' | 'likes' | 'follows' | 'system') => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [key]: !prev.notifications[key],
      },
    }));
    setDirty(true);
  };

  const handleContentToggle = (key: keyof CommunitySettingsState['content']) => {
    setSettings((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [key]: !prev.content[key],
      },
    }));
    setDirty(true);
  };

  const handlePrivacyToggle = (key: 'allowMessages') => {
    setSettings((prev) => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [key]: !prev.privacy[key],
      },
    }));
    setDirty(true);
  };

  const handleDigestChange = (value: DigestFrequency) => {
    setSettings((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        digest: value,
      },
    }));
    setDirty(true);
  };

  const handleVisibilityChange = (value: 'public' | 'followers' | 'private') => {
    setSettings((prev) => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        profileVisibility: value,
      },
    }));
    setDirty(true);
  };

  const handleSave = () => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    setDirty(false);
    setLastSavedAt(new Date().toLocaleString());
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setDirty(true);
  };

  const digestLabel = useMemo(() => {
    switch (settings.notifications.digest) {
      case 'daily':
        return '每日摘要';
      case 'weekly':
        return '每周一封';
      default:
        return '关闭邮件摘要';
    }
  }, [settings.notifications.digest]);

  return (
    <div className="community-settings">
      <header>
        <h1>社区设置</h1>
        <p>自定义你的通知提醒、内容过滤与隐私偏好。</p>
      </header>

      <section className="community-settings__panel">
        <h2>通知偏好</h2>
        <div className="community-settings__grid">
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.mentions}
              onChange={() => handleNotificationToggle('mentions')}
            />
            @提及与评论提醒
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.likes}
              onChange={() => handleNotificationToggle('likes')}
            />
            点赞通知
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.follows}
              onChange={() => handleNotificationToggle('follows')}
            />
            新粉丝通知
          </label>
          <label>
            <input
              type="checkbox"
              checked={settings.notifications.system}
              onChange={() => handleNotificationToggle('system')}
            />
            系统公告
          </label>
        </div>

        <div className="community-settings__digest">
          <span>邮件摘要：{digestLabel}</span>
          <div>
            <label>
              <input
                type="radio"
                name="digest"
                checked={settings.notifications.digest === 'daily'}
                onChange={() => handleDigestChange('daily')}
              />
              每日
            </label>
            <label>
              <input
                type="radio"
                name="digest"
                checked={settings.notifications.digest === 'weekly'}
                onChange={() => handleDigestChange('weekly')}
              />
              每周
            </label>
            <label>
              <input
                type="radio"
                name="digest"
                checked={settings.notifications.digest === 'off'}
                onChange={() => handleDigestChange('off')}
              />
              关闭
            </label>
          </div>
        </div>
      </section>

      <section className="community-settings__panel">
        <h2>内容偏好</h2>
        <label className="community-settings__switch">
          <input
            type="checkbox"
            checked={settings.content.safeMode}
            onChange={() => handleContentToggle('safeMode')}
          />
          启用内容安全模式（屏蔽敏感或低质信息）
        </label>
        <label className="community-settings__switch">
          <input
            type="checkbox"
            checked={settings.content.showFollowedOnly}
            onChange={() => handleContentToggle('showFollowedOnly')}
          />
          时间线优先显示关注作者
        </label>
        <label className="community-settings__switch">
          <input
            type="checkbox"
            checked={settings.content.muteCommercial}
            onChange={() => handleContentToggle('muteCommercial')}
          />
          屏蔽营销内容实验功能
        </label>
      </section>

      <section className="community-settings__panel">
        <h2>隐私与私信</h2>
        <div className="community-settings__radios">
          <label>
            <input
              type="radio"
              name="visibility"
              checked={settings.privacy.profileVisibility === 'public'}
              onChange={() => handleVisibilityChange('public')}
            />
            对所有人公开主页
          </label>
          <label>
            <input
              type="radio"
              name="visibility"
              checked={settings.privacy.profileVisibility === 'followers'}
              onChange={() => handleVisibilityChange('followers')}
            />
            仅对关注者展示详情
          </label>
          <label>
            <input
              type="radio"
              name="visibility"
              checked={settings.privacy.profileVisibility === 'private'}
              onChange={() => handleVisibilityChange('private')}
            />
            设为完全私密
          </label>
        </div>

        <label className="community-settings__switch">
          <input
            type="checkbox"
            checked={settings.privacy.allowMessages}
            onChange={() => handlePrivacyToggle('allowMessages')}
          />
          允许陌生人发起私信
        </label>
      </section>

      <footer className="community-settings__actions">
        <button type="button" onClick={handleReset}>
          恢复默认
        </button>
        <button type="button" onClick={handleSave} disabled={!dirty}>
          保存设置
        </button>
        <span>{lastSavedAt ? `上次保存：${lastSavedAt}` : '尚未保存'}</span>
      </footer>
    </div>
  );
};

export default SettingsPage;
